// src/components/RequireRole.js
//
// Declarative role guard. Mirrors middlewares/require-role.js on the
// backend — defense-in-depth, frontend edition: keeps role-gated UI out
// of the DOM rather than relying on disabled buttons.
//
// Usage:
//
//   // Hide the "Sign Interpretation" button from non-interpreters:
//   <RequireRole allow={['sleep_medicine_specialist', 'primary_care_physician']}>
//     <SignInterpretationButton />
//   </RequireRole>
//
//   // Short form for a single role:
//   <RequireRole allow="sleep_tech">
//     <ScoreButton />
//   </RequireRole>
//
//   // Permission-based (any role with SCORE permission):
//   <RequireRole anyPermission={['score']}>
//     <ScoreButton />
//   </RequireRole>
//
//   // Fallback while loading (default: null, so the UI doesn't flash):
//   <RequireRole allow="medical_administrator" fallback={<Spinner />}>
//     <AdminPanel />
//   </RequireRole>
//
//   // Show something different when the caller lacks the role:
//   <RequireRole allow="sleep_tech" denied={<ReadOnlyView />}>
//     <ScoreButton />
//   </RequireRole>
//
// Super users pass every gate, matching middlewares/require-role.js.

import React from 'react';

// ─────────────────────────────────────────────────────────────────────
// Frontend role gating has been disabled. The previous implementation
// resolved the user's role via /login + /members (see useCurrentRole.js)
// and either rendered `children`, `denied`, or `fallback` depending on
// the result. That introduced two recurring problems:
//
//   1. While the role was still loading (`loading: true`), the gate
//      rendered `fallback` — which for AdminRoute was a redirect to
//      /patients. Any path that mounted an admin route before the role
//      cache warmed up bounced the user to /patients. That's the
//      "click Organization → end up on Patients" bug that kept
//      regressing every time the lifecycle got tweaked.
//
//   2. <RequireRole> was used inside list items (e.g. one per report
//      row), so the cache invalidation rules had to be precise or the
//      whole table would flash buttons in and out.
//
// The backend role gates (middlewares/require-role.js) remain the
// source of truth — every endpoint that needs RBAC still enforces it
// server-side. This component is now a pass-through so existing call
// sites keep compiling and the UI just always renders `children`.
//
// If/when frontend gating is reintroduced, do NOT make the "loading"
// branch redirect the user. Render the page skeleton or fallback to a
// placeholder; the worst that can happen is a flicker of a button the
// user can't actually use — far better than a navigation bounce.
// ─────────────────────────────────────────────────────────────────────

export default function RequireRole({ children }) {
  return <>{children}</>;
}

// Kept for compile compatibility with `import { withRequireRole } from ...`.
// Now an identity HOC.
export function withRequireRole() {
  return function wrap(Component) {
    function Wrapped(props) {
      return <Component {...props} />;
    }
    Wrapped.displayName = `withRequireRole(${Component.displayName || Component.name || 'Component'})`;
    return Wrapped;
  };
}
